/**
 * LoginPage - Authentication page using Simulacra design system
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { YStack, H1, H2, Text } from 'tamagui';
import { GlassCard, MatrixButton } from '../components/base';

const API_URL = 'https://nings-api.beamer-labs.workers.dev';

interface LoginPageProps {
  isLoggedIn: boolean;
}

const LoginPage: React.FC<LoginPageProps> = ({ isLoggedIn }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLimitReached(false);
    setLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/auth/passwordless/create?email=${encodeURIComponent(email)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.status === 'OK') {
        setSuccess(true);
        setEmail(''); // Clear email field after success
      } else if (data.status === 'SIGNUP_LIMIT_REACHED') {
        setLimitReached(true);
        setWaitlistEmail(email); // Pre-fill waitlist form
      } else {
        setError(data.detail || 'Failed to send magic link');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/waitlist/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: waitlistEmail })
      });

      const data = await response.json();

      if (response.ok && data.status === 'OK') {
        setSuccess(true);
        setLimitReached(false);
        setWaitlistEmail('');
      } else {
        setError(data.message || 'Failed to join waitlist');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <YStack flex={1} backgroundColor="$background" minHeight="100vh" alignItems="center" justifyContent="center" padding="$4">
      <YStack gap="$6" maxWidth={450} width="100%" padding="$6">
        {/* Header */}
        <YStack gap="$2" alignItems="center">
          <H1 color="$green10" fontFamily="$heading" textAlign="center" fontSize="$8">
            NINGS
          </H1>
          <Text color="$colorPress" fontFamily="$body" fontSize="$4" textAlign="center">
            Sign in with a magic link — no password needed
          </Text>
        </YStack>

        {/* Limit reached state */}
        {limitReached ? (
          <>
            <GlassCard backgroundColor="$backgroundPress" borderColor="$orange10" borderWidth={2}>
              <YStack gap="$4" alignItems="center" paddingVertical="$6">
                <Text fontSize="$9">🚀</Text>
                <H2 color="$orange10" fontFamily="$heading" textAlign="center">
                  Beta Signup Limit Reached
                </H2>
                <Text color="$color" fontFamily="$body" fontSize="$3" textAlign="center" lineHeight="$2">
                  We're at capacity for our initial beta test (25 users).
                  {'\n\n'}
                  Join our waitlist and you'll be among the first to know when we open up more spots!
                </Text>
              </YStack>
            </GlassCard>

            <GlassCard>
              <form onSubmit={handleJoinWaitlist}>
                <YStack gap="$4">
                  <YStack gap="$2">
                    <Text
                      fontSize="$2"
                      fontWeight="500"
                      color="$color"
                      fontFamily="$mono"
                      textTransform="uppercase"
                    >
                      Join the waitlist
                    </Text>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: 'rgba(20, 20, 20, 0.6)',
                        border: '1px solid rgba(125, 211, 192, 0.2)',
                        borderRadius: '8px',
                        color: '#e0e0e0',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '16px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </YStack>

                  {error && (
                    <GlassCard backgroundColor="$backgroundPress" borderColor="$red10" borderWidth={2}>
                      <Text color="$red10" fontFamily="$mono" fontSize="$3">
                        {error}
                      </Text>
                    </GlassCard>
                  )}

                  <MatrixButton
                    variant="primary"
                    disabled={loading || !waitlistEmail.trim()}
                    onPress={handleJoinWaitlist}
                  >
                    {loading ? 'Joining waitlist...' : 'Join waitlist'}
                  </MatrixButton>

                  <MatrixButton
                    variant="ghost"
                    onPress={() => setLimitReached(false)}
                  >
                    Back to login
                  </MatrixButton>
                </YStack>
              </form>
            </GlassCard>
          </>
        ) : success ? (
          <GlassCard backgroundColor="$backgroundHover" borderColor="$cyan10" borderWidth={2}>
            <YStack gap="$3" alignItems="center" paddingVertical="$4">
              <Text fontSize="$9">✉️</Text>
              <H2 color="$green10" fontFamily="$heading" textAlign="center">
                Check your email!
              </H2>
              <Text color="$color" fontFamily="$body" fontSize="$3" textAlign="center" lineHeight="$2">
                We sent a magic link to your inbox.
                {'\n'}
                Click it to sign in instantly.
              </Text>

              {/* Inline spam warning */}
              <Text color="$cyan10" fontFamily="$mono" fontSize="$3" fontWeight="600" textAlign="center" marginTop="$3">
                (Check spam if you don't see it!)
              </Text>

              <MatrixButton
                variant="secondary"
                onPress={() => setSuccess(false)}
                marginTop="$2"
              >
                Send another link
              </MatrixButton>
            </YStack>
          </GlassCard>
        ) : (
          <GlassCard>
            <form onSubmit={handleSendMagicLink}>
              <YStack gap="$4">
                {/* Email input */}
                <YStack gap="$2">
                  <Text
                    fontSize="$2"
                    fontWeight="500"
                    color="$color"
                    fontFamily="$mono"
                    textTransform="uppercase"
                  >
                    Email address
                  </Text>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'rgba(20, 20, 20, 0.6)',
                      border: '1px solid rgba(125, 211, 192, 0.2)',
                      borderRadius: '8px',
                      color: '#e0e0e0',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '16px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </YStack>

                {/* Error message */}
                {error && (
                  <GlassCard backgroundColor="$backgroundPress" borderColor="$red10" borderWidth={2}>
                    <Text color="$red10" fontFamily="$mono" fontSize="$3">
                      {error}
                    </Text>
                  </GlassCard>
                )}

                {/* Submit button */}
                <MatrixButton
                  variant="primary"
                  disabled={loading || !email.trim()}
                  onPress={handleSendMagicLink}
                >
                  {loading ? 'Sending magic link...' : 'Send magic link'}
                </MatrixButton>
              </YStack>
            </form>
          </GlassCard>
        )}

        {/* Footer info */}
        <GlassCard backgroundColor="$backgroundHover" borderColor="$borderPastel">
          <Text fontSize="$2" color="$placeholderColor" fontFamily="$body" textAlign="center" lineHeight="$1">
            By signing in, you'll get instant access to AI-powered
            {'\n'}
            earnings analysis for any stock ticker.
          </Text>
        </GlassCard>
      </YStack>
    </YStack>
  );
};

export default LoginPage;
